(() => {
  'use strict';

  const doc = document;
  const body = doc.body;
  const drawer = doc.querySelector('[data-mobile-drawer]');
  const drawerToggle = doc.querySelector('[data-drawer-toggle]');
  const drawerClose = doc.querySelector('[data-drawer-close]');
  const cartDrawer = doc.querySelector('[data-cart-drawer]');
  const cartDrawerPanel = cartDrawer?.querySelector('.bleen-cart-drawer__panel');
  const cartDrawerItems = cartDrawer?.querySelector('[data-cart-drawer-items]');
  const cartDrawerFooter = cartDrawer?.querySelector('[data-cart-drawer-footer]');
  const cartDrawerSubtotal = cartDrawer?.querySelector('[data-cart-drawer-subtotal]');
  const toast = doc.querySelector('[data-bleen-toast]');
  const modalBackground = [
    doc.querySelector('.bleen-header-shell'),
    doc.querySelector('#MainContent'),
    doc.querySelector('.bleen-footer')
  ].filter(Boolean);
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let lastFocusedElement = null;
  let cartLastFocusedElement = null;
  let toastTimer = null;

  const syncModalBackground = () => {
    const modalOpen = (drawer && !drawer.hidden) || (cartDrawer && !cartDrawer.hidden);
    modalBackground.forEach((element) => element.toggleAttribute('inert', Boolean(modalOpen)));
  };

  const trapFocus = (event, container) => {
    if (event.key !== 'Tab' || !container) return;
    const focusable = [...container.querySelectorAll(focusableSelector)].filter((element) => element.getClientRects().length > 0);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && doc.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && doc.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const setDrawer = (open) => {
    if (!drawer || !drawerToggle) return;
    if (open && cartDrawer && !cartDrawer.hidden) setCartDrawer(false);
    drawer.hidden = !open;
    drawerToggle.setAttribute('aria-expanded', String(open));
    body.classList.toggle('bleen-drawer-open', open);
    syncModalBackground();

    if (open) {
      lastFocusedElement = doc.activeElement;
      requestAnimationFrame(() => drawerClose?.focus());
    } else if (lastFocusedElement) {
      lastFocusedElement.focus();
    }
  };

  drawerToggle?.addEventListener('click', () => setDrawer(drawer?.hidden ?? true));
  drawerClose?.addEventListener('click', () => setDrawer(false));
  drawer?.addEventListener('click', (event) => {
    if (event.target.closest('[data-drawer-overlay], a')) setDrawer(false);
  });

  doc.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && drawer && !drawer.hidden) setDrawer(false);
    if (event.key === 'Escape' && cartDrawer && !cartDrawer.hidden) setCartDrawer(false);
    if (event.key === 'Tab') {
      if (cartDrawer && !cartDrawer.hidden) trapFocus(event, cartDrawerPanel);
      else if (drawer && !drawer.hidden) trapFocus(event, drawer.querySelector('.bleen-mobile-drawer__panel'));
    }
  });

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'EUR';
    try {
      return new Intl.NumberFormat(doc.documentElement.lang || 'it-IT', { style: 'currency', currency }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `€${((Number(cents) || 0) / 100).toFixed(2).replace('.', ',')}`;
    }
  };

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;'
  })[character]);

  const cartTitles = {
    'bleen-ammorbidente-concentrato-con-microcapsule-apocalipse': 'Apocalipse',
    'bleen-ammorbidente-concentrato-con-formula-professionale-fresco-abbraccio': 'Fresco Abbraccio — New Formula',
    'bleen-ammorbidente-tocco-vivace-new-formula': 'Tocco Vivace — New Formula',
    'bleen-ammorbidente-via-lattea': 'Via Lattea',
    'bleen-fresco-abbraccio': 'Fresco Abbraccio',
    'bleen-soffio-caldo': 'Soffio Caldo',
    'bleen-tocco-vivace': 'Tocco Vivace',
    'bleen-forza-brillante': 'Forza Brillante',
    'bleen-lavatrice-bianco-assoluto': 'Bianco Assoluto',
    'bleen-carezza-floreale': 'Carezza Floreale',
    'bleen-note-di-rugiada': 'Note di Rugiada',
    'bleen-detersivo-lavatrice-via-lattea': 'Detersivo Via Lattea',
    'bleen-aria-destate': "Lavapavimenti Aria d'Estate",
    'bleen-essenza-delicata': 'Lavapavimenti Essenza Delicata',
    'bleen-profumo-doriente': "Lavapavimenti Profumo d'Oriente",
    'bleen-aria-d-estate': 'Multiuso Pulito Lucente',
    'bleen-sgrassatore': 'Sgrassatore Marsiglia',
    'offerta-1x-ammorbidente-fresco-abbraccio-1x-detersivo-bianco-assoluto-1x-spray-sgrassatore-marsiglia-1x-pavimenti-profumo-d-oriente': 'Kit Casa Fresco Abbraccio',
    'offerta-1x-ammorbidente-soffio-caldo-1x-detersivo-carezza-floreale-1x-spray-sgrassatore-marsiglia-1x-pavimenti-profumo-d-oriente': 'Kit Casa Soffio Caldo',
    'offerta-ammorbidente-1x-via-lattea-1x-detersivo-carezza-floreale-1x-spray-multiuso-pulito-lucente-1x-pavimenti-essenza-delicata': 'Kit Casa Via Lattea & Carezza Floreale',
    'offerta-ammorbidente-1x-via-lattea-1x-detersivo-via-lattea': 'Duo Bucato Via Lattea',
    'offerta-ammorbidente-via-lattea-detersivo-via-lattea-spray-sgrassatore-marsiglia-pavimenti-aria-d-estate-1': 'Kit Casa Via Lattea'
  };

  const renderCartDrawer = (cart) => {
    if (!cartDrawerItems || !cartDrawerFooter || !cartDrawerSubtotal) return;
    cartDrawerSubtotal.textContent = formatMoney(cart.total_price);

    if (!cart.items?.length) {
      cartDrawerItems.innerHTML = '<div class="bleen-cart-drawer__empty"><h3>Il carrello è vuoto</h3><p>Scopri i prodotti Bleen pensati per la cura della casa.</p><a class="bleen-button bleen-button--secondary" href="/collections/all">Scopri i prodotti</a></div>';
      cartDrawerFooter.hidden = true;
      return;
    }

    cartDrawerItems.innerHTML = cart.items.map((item, index) => `
      <article class="bleen-cart-drawer-item">
        <a class="bleen-cart-drawer-item__image" href="${escapeHtml(item.url)}">
          ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(cartTitles[item.handle] || item.product_title)}" width="160" height="160">` : ''}
        </a>
        <div>
          <h3><a href="${escapeHtml(item.url)}">${escapeHtml(cartTitles[item.handle] || item.product_title)}</a></h3>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<p>${escapeHtml(item.variant_title)}</p>` : ''}
          <span>${item.quantity} × ${formatMoney(item.final_price)}</span>
          <button type="button" data-cart-remove="${index + 1}">Rimuovi</button>
        </div>
        <strong>${formatMoney(item.final_line_price)}</strong>
      </article>
    `).join('');
    cartDrawerFooter.hidden = false;
  };

  const setCartDrawer = (open, cart = null) => {
    if (!cartDrawer) return;
    if (open && drawer && !drawer.hidden) setDrawer(false);
    cartDrawer.hidden = !open;
    body.classList.toggle('bleen-cart-drawer-open', open);
    syncModalBackground();
    if (open) {
      cartLastFocusedElement = doc.activeElement;
      if (cart) renderCartDrawer(cart);
      requestAnimationFrame(() => cartDrawer.querySelector('[data-cart-drawer-close]')?.focus());
    } else {
      cartLastFocusedElement?.focus();
    }
  };

  const getCart = async () => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Impossibile aggiornare il carrello');
    return response.json();
  };

  doc.addEventListener('click', async (event) => {
    const cartOpen = event.target.closest('[data-cart-open]');
    if (cartOpen) {
      event.preventDefault();
      setCartDrawer(true);
      try { renderCartDrawer(await getCart()); } catch (_) { window.location.href = cartOpen.href; }
      return;
    }

    if (event.target.closest('[data-cart-drawer-close]')) {
      setCartDrawer(false);
      return;
    }

    const remove = event.target.closest('[data-cart-remove]');
    if (!remove) return;
    remove.disabled = true;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(remove.dataset.cartRemove), quantity: 0 })
      });
      if (!response.ok) throw new Error('Impossibile rimuovere il prodotto');
      const cart = await response.json();
      renderCartDrawer(cart);
      doc.querySelectorAll('[data-cart-count]').forEach((node) => {
        node.textContent = cart.item_count;
        node.hidden = cart.item_count === 0;
      });
    } catch (error) {
      showToast(error.message || 'Si è verificato un errore', true);
      remove.disabled = false;
    }
  });

  doc.addEventListener('change', (event) => {
    const sort = event.target.closest('[data-collection-sort]');
    if (sort) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      window.location.href = url.toString();
      return;
    }

    const variantSelect = event.target.closest('[data-product-variant-select]');
    if (!variantSelect) return;

    const option = variantSelect.options[variantSelect.selectedIndex];
    const productInfo = variantSelect.closest('.bleen-product-info');
    const available = option?.dataset.available === 'true';
    const price = productInfo?.querySelector('[data-product-price]');
    const compare = productInfo?.querySelector('[data-product-compare]');
    const submit = productInfo?.querySelector('[data-product-submit]');
    const availability = productInfo?.querySelector('[data-product-availability]');

    if (price && option?.dataset.price) price.textContent = option.dataset.price;
    if (compare) {
      compare.textContent = option?.dataset.compare || '';
      compare.hidden = !option?.dataset.compare || option.dataset.compare === option.dataset.price;
    }
    if (submit) {
      submit.disabled = !available;
      submit.textContent = available ? 'Aggiungi al carrello' : 'Non disponibile';
    }
    if (availability) {
      availability.textContent = available ? 'Disponibile — pronto per la spedizione' : 'Momentaneamente non disponibile';
    }
  });

  doc.addEventListener('click', (event) => {
    const bulkPreset = event.target.closest('[data-bulk-preset]');
    if (bulkPreset) {
      const form = bulkPreset.closest('[data-bulk-form]');
      const offer = bulkPreset.closest('[data-bulk-offer]');
      const quantityInput = form?.querySelector('[data-bulk-quantity]');
      const totalNode = offer?.querySelector('[data-bulk-total]');
      const subtotalNode = offer?.querySelector('[data-bulk-subtotal]');
      const unitNode = offer?.querySelector('[data-bulk-unit]');
      const savingNode = offer?.querySelector('[data-bulk-saving]');
      const submit = form?.querySelector('[data-bulk-submit]');
      const quantity = Math.max(1, Number(bulkPreset.dataset.quantity || 1));
      const discount = Math.min(100, Math.max(0, Number(bulkPreset.dataset.discount || 0)));
      const unitPrice = Math.max(0, Number(offer?.dataset.unitPrice || 0));
      const subtotal = unitPrice * quantity;
      const total = Math.round(subtotal * (100 - discount) / 100);
      const saving = subtotal - total;

      offer?.querySelectorAll('[data-bulk-preset]').forEach((preset) => {
        const active = preset === bulkPreset;
        preset.classList.toggle('is-active', active);
        preset.setAttribute('aria-pressed', String(active));
      });

      if (quantityInput) quantityInput.value = String(quantity);
      if (totalNode) totalNode.textContent = formatMoney(total);
      if (subtotalNode) {
        subtotalNode.textContent = formatMoney(subtotal);
        subtotalNode.hidden = saving <= 0;
      }
      if (unitNode && form) {
        const unitLabel = form.dataset.unitLabel || '[price] cad.';
        unitNode.textContent = unitLabel.replace('[price]', formatMoney(Math.round(total / quantity)));
      }
      if (savingNode) {
        const savingLabel = form?.dataset.savingLabel || 'Risparmi [amount]';
        savingNode.textContent = savingLabel.replace('[amount]', formatMoney(saving));
        savingNode.hidden = saving <= 0;
      }
      if (submit && form) {
        const singleLabel = form.dataset.singleLabel || 'Aggiungi al carrello';
        const multipleLabel = form.dataset.multipleLabel || 'Aggiungi [quantity] al carrello';
        submit.textContent = quantity === 1 ? singleLabel : multipleLabel.replace('[quantity]', String(quantity));
      }
      return;
    }

    const thumbnail = event.target.closest('[data-product-thumb]');
    if (thumbnail) {
      const gallery = thumbnail.closest('[data-product-gallery]');
      const mainImage = gallery?.querySelector('[data-product-main-image]');
      if (mainImage && thumbnail.dataset.imageUrl) {
        mainImage.src = thumbnail.dataset.imageUrl;
        mainImage.removeAttribute('srcset');
        mainImage.alt = thumbnail.dataset.imageAlt || '';
        gallery.querySelectorAll('[data-product-thumb]').forEach((node) => node.classList.toggle('is-active', node === thumbnail));
      }
      return;
    }

    const quantityButton = event.target.closest('[data-quantity-change]');
    if (!quantityButton) return;
    const quantity = quantityButton.parentElement?.querySelector('[data-quantity-input]');
    if (!quantity) return;
    const delta = Number(quantityButton.dataset.quantityChange || 0);
    const minimum = Number(quantity.min || 1);
    quantity.value = String(Math.max(minimum, Number(quantity.value || minimum) + delta));
  });

  const showToast = (message, isError = false) => {
    if (!toast) return;
    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.classList.toggle('is-error', isError);
    toast.classList.add('is-visible');
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 3200);
  };

  const updateCartCount = async () => {
    try {
      const cart = await getCart();
      doc.querySelectorAll('[data-cart-count]').forEach((node) => {
        node.textContent = cart.item_count;
        node.hidden = cart.item_count === 0;
      });
      doc.dispatchEvent(new CustomEvent('bleen:cart-updated', { detail: cart }));
      return cart;
    } catch (_) {
      // Il collegamento al carrello resta comunque utilizzabile senza JavaScript.
      return null;
    }
  };

  doc.addEventListener('submit', async (event) => {
    const quiz = event.target.closest('[data-bleen-quiz]');
    if (quiz) {
      event.preventDefault();
      const results = doc.querySelector('[data-quiz-results]');
      const profile = new FormData(quiz).get('vibe');
      if (!results || !profile) return;
      results.hidden = false;
      results.querySelectorAll('[data-quiz-result]').forEach((result) => {
        result.hidden = result.dataset.quizResult !== profile;
      });
      results.setAttribute('tabindex', '-1');
      results.focus({ preventScroll: true });
      results.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const form = event.target.closest('form[data-quick-add]');
    if (!form) return;

    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const originalLabel = button?.textContent;

    try {
      if (button) {
        button.disabled = true;
        button.textContent = 'Aggiungo…';
      }

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });

      if (!response.ok) throw new Error('Impossibile aggiungere il prodotto');
      const cart = await updateCartCount();
      showToast('Prodotto aggiunto al carrello');
      if (cart) setCartDrawer(true, cart);
    } catch (error) {
      showToast(error.message || 'Si è verificato un errore', true);
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = originalLabel;
      }
    }
  });

  doc.addEventListener('shopify:section:load', updateCartCount);
})();

/* BLEEN_VALUE_CAROUSEL_INFINITY */
(() => {
  const mobileQuery = window.matchMedia('(max-width: 749px)');

  const centerItem = (track, item, behavior = 'smooth') => {
    const left = item.offsetLeft - track.offsetLeft - (track.clientWidth - item.offsetWidth) / 2;
    track.scrollTo({ left, behavior });
  };

  const initValueCarousels = (root = document) => {
    root.querySelectorAll('[data-bleen-value-carousel]').forEach((carousel) => {
      if (carousel.dataset.bleenInfinityReady === 'true') return;

      const track = carousel.querySelector('[data-bleen-value-track]');
      const originals = Array.from(track.querySelectorAll('.bleen-value-item'));
      const previous = carousel.querySelector('[data-bleen-value-prev]');
      const next = carousel.querySelector('[data-bleen-value-next]');
      const dots = Array.from(carousel.querySelectorAll('[data-bleen-value-dot]'));
      const status = carousel.querySelector('[data-bleen-value-status]');
      const cloneCount = Math.min(2, originals.length);
      let activeIndex = 0;
      let frame;
      let settleTimer;

      originals.forEach((item, index) => {
        item.dataset.bleenValueIndex = String(index);
      });

      const before = document.createDocumentFragment();
      originals.slice(-cloneCount).forEach((item) => {
        const clone = item.cloneNode(true);
        clone.dataset.bleenClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        before.appendChild(clone);
      });
      track.insertBefore(before, track.firstChild);

      originals.slice(0, cloneCount).forEach((item) => {
        const clone = item.cloneNode(true);
        clone.dataset.bleenClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      });

      const allItems = Array.from(track.querySelectorAll('.bleen-value-item'));

      const update = (index) => {
        activeIndex = (index + originals.length) % originals.length;
        allItems.forEach((item) => {
          item.classList.toggle('is-active', Number(item.dataset.bleenValueIndex) === activeIndex);
        });
        dots.forEach((dot, dotIndex) => {
          dot.classList.toggle('is-active', dotIndex === activeIndex);
          dot.setAttribute('aria-current', dotIndex === activeIndex ? 'true' : 'false');
        });
        status.textContent = activeIndex + 1 + ' / ' + originals.length;
        previous.disabled = false;
        next.disabled = false;
      };

      const nearestItem = () => {
        const center = track.scrollLeft + track.clientWidth / 2;
        let closest = allItems[0];
        let distance = Infinity;
        allItems.forEach((item) => {
          const itemCenter = item.offsetLeft - track.offsetLeft + item.offsetWidth / 2;
          const delta = Math.abs(itemCenter - center);
          if (delta < distance) {
            distance = delta;
            closest = item;
          }
        });
        return closest;
      };

      const move = (direction) => {
        const current = nearestItem();
        const currentPosition = allItems.indexOf(current);
        const target = allItems[Math.max(0, Math.min(currentPosition + direction, allItems.length - 1))];
        update(Number(target.dataset.bleenValueIndex));
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        centerItem(track, target, reducedMotion ? 'auto' : 'smooth');
      };

      const goTo = (index) => {
        const targetIndex = (index + originals.length) % originals.length;
        update(targetIndex);
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        centerItem(track, originals[targetIndex], reducedMotion ? 'auto' : 'smooth');
      };

      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      dots.forEach((dot, index) => dot.addEventListener('click', () => goTo(index)));

      track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          move(1);
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          move(-1);
        }
      });

      track.addEventListener('scroll', () => {
        window.cancelAnimationFrame(frame);
        window.clearTimeout(settleTimer);
        frame = window.requestAnimationFrame(() => {
          const closest = nearestItem();
          update(Number(closest.dataset.bleenValueIndex));
        });

        settleTimer = window.setTimeout(() => {
          const closest = nearestItem();
          if (closest.dataset.bleenClone === 'true') {
            const logicalIndex = Number(closest.dataset.bleenValueIndex);
            centerItem(track, originals[logicalIndex], 'auto');
          }
        }, 140);
      }, { passive: true });

      const centerCurrent = () => {
        if (mobileQuery.matches) centerItem(track, originals[activeIndex], 'auto');
      };

      mobileQuery.addEventListener('change', centerCurrent);
      carousel.dataset.bleenInfinityReady = 'true';
      update(0);
      window.requestAnimationFrame(() => window.requestAnimationFrame(centerCurrent));
    });
  };

  initValueCarousels();
  document.addEventListener('shopify:section:load', (event) => initValueCarousels(event.target));
})();


/* BLEEN_HEADER_HOVER_DROPDOWN */
(() => {
  const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 901px)');

  const initHoverMenus = (root = document) => {
    root.querySelectorAll('.bleen-nav details').forEach((details) => {
      if (details.dataset.bleenHoverReady === 'true') return;

      const summary = details.querySelector('summary');
      let closeTimer;

      const closeNow = (item) => {
        window.clearTimeout(item._bleenCloseTimer);
        item.classList.remove('is-closing');
        item.open = false;
      };

      const openMenu = () => {
        if (!hoverQuery.matches) return;
        window.clearTimeout(closeTimer);
        document.querySelectorAll('.bleen-nav details[open]').forEach((item) => {
          if (item !== details) closeNow(item);
        });
        details.classList.remove('is-closing');
        details.open = true;
      };

      const closeMenu = () => {
        if (!hoverQuery.matches || !details.open) return;
        window.clearTimeout(closeTimer);
        details.classList.add('is-closing');
        closeTimer = window.setTimeout(() => {
          details.open = false;
          details.classList.remove('is-closing');
        }, 170);
        details._bleenCloseTimer = closeTimer;
      };

      details.addEventListener('mouseenter', openMenu);
      details.addEventListener('mouseleave', closeMenu);
      details.addEventListener('focusin', openMenu);
      details.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!details.contains(document.activeElement)) closeMenu();
        }, 0);
      });
      summary.addEventListener('click', () => {
        window.clearTimeout(closeTimer);
        details.classList.remove('is-closing');
      });

      details.dataset.bleenHoverReady = 'true';
    });
  };

  initHoverMenus();
  document.addEventListener('shopify:section:load', (event) => initHoverMenus(event.target));
})();
